import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { GqlCourse } from "~/__generated__";
import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaDataGrid from "~/modules/app/components/GaiaTable";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import CourseUserProtocolForm from "~/modules/settings/course/components/CourseUserProtocol";
import useUserCourseCreate from "~/modules/settings/course/routes/CourseEditRoute/api/useUserCourseCreate";
import useUserCourseDelete from "~/modules/settings/course/routes/CourseEditRoute/api/useUserCourseDelete";

type Props = {
    data: GqlCourse[];
};

export default function UserCourseList({ data }: Props) {
    const { t } = useTranslation("user");
    const { userId } = useParams();

    if (!userId) throw new Error("No User ID in Route, Routing is not configured correctly");

    const { deleteUserCourse, loading: deleteLoading, error: deleteError } = useUserCourseDelete();

    const { createUserCourse, loading: createLoading, error: createError } = useUserCourseCreate();

    const handleUserCourseDelete = async (courseId: string) => {
        const deletedCourse = data.find(course => course.id === courseId);
        if (!deletedCourse) return;
        const { id } = deletedCourse;
        await deleteUserCourse({
            userId,
            courseId: id,
        });
    };

    if (!data && !deleteLoading && (deleteError || createError)) {
        return <ErrorScreen error={deleteError || createError} />;
    }

    return (
        <>
            <CourseUserProtocolForm
                excludeAssignedToUserId={userId}
                onSubmitted={(courseId: string): void => {
                    createUserCourse({
                        userId: userId,
                        courseId,
                    });
                }}
            />
            <GaiaDataGrid
                rows={data}
                columns={[
                    {
                        field: "name",
                        headerName: t("userCourseList.colName"),
                        flex: 1,
                    },
                    {
                        type: "actions",
                        field: "actions",
                        headerName: t("userCourseList.colDelete"),
                        flex: 1,
                        renderCell: params => (
                            <GaiaDeleteButton
                                label=""
                                isLoading={deleteLoading}
                                onDelete={() => handleUserCourseDelete(params.row.id)}
                            />
                        ),
                    },
                ]}
                loading={deleteLoading || createLoading}
                rowCount={data.length}
            />
        </>
    );
}
